// src/components/Discussion.jsx
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TOASTS } from "../utils/constants";
import { collectAttachments, collectLinks } from "../utils/attachments";
import { Lightbox } from "./Lightbox";

import { DiscussionProvider } from "./discussion/Provider";
import { CommentPolicy } from "./discussion/CommentPolicy";
import { useCommentList } from "../hooks";
import { useChatReadLookup } from "../hooks/useChatStats";
import {
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useReactMutation,
  usePinCommentMutation,
} from "../api/queries/comments";
import { useMarkChatReadMutation } from "../api/queries/chat-reads";
import DiscussionHeader from "./discussion/DiscussionHeader";
import SortToolbar from "./discussion/SortToolbar";
import CommentTree from "./discussion/CommentTree";
import CommentComposer from "./discussion/CommentComposer";
import CommentFiles from "./discussion/CommentFiles";
import CommentLinks from "./discussion/CommentLinks";

function useScrollToComment(comments) {
  const pendingIdRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const doScroll = useCallback((id) => {
    const el = document.getElementById(`comment-${id}`);
    if (!el) return false;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    el.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
    el.classList.add("comment-flash");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (el.isConnected) el.classList.remove("comment-flash");
    }, 2000);
    return true;
  }, []);

  useLayoutEffect(() => {
    const id = pendingIdRef.current;
    if (!id) return;
    if (doScroll(id)) pendingIdRef.current = null;
  }, [comments, doScroll]);

  return useCallback(
    (id) => {
      if (doScroll(id)) {
        pendingIdRef.current = null;
      } else {
        pendingIdRef.current = id;
      }
    },
    [doScroll],
  );
}

export default function Discussion({
  projectId = null,
  taskId = null,
  currentUser,
  candidates = [],
  toast,
  employees = [],
  onTaskClick = null,
  showTaskLink = false,
  tasks = [],
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("new");
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [lightbox, setLightbox] = useState({ index: null, list: [] });
  const [viewMode, setViewMode] = useState("chat");

  // Серверный источник «прочитано до» — без локального cutoff.
  const { isUnread: isCommentUnread } = useChatReadLookup(currentUser.id);

  // Отметка «прочитано» при монтировании чата.
  const { mutate: markChatRead } = useMarkChatReadMutation();
  useEffect(() => {
    markChatRead({ projectId, taskId });
  }, [markChatRead, projectId, taskId]);

  const { comments, visibleComments, matchSteps } = useCommentList(
    projectId,
    taskId,
    searchQuery,
    sortOrder,
  );

  const attachments = useMemo(
    () => collectAttachments(comments, { projectId, taskId }),
    [comments, projectId, taskId],
  );

  const links = useMemo(
    () => collectLinks(comments, { projectId, taskId }),
    [comments, projectId, taskId],
  );

  const viewTabs = useMemo(
    () => [
      { id: "files", label: "Файлы", count: attachments.length },
      { id: "links", label: "Ссылки", count: links.length },
    ],
    [attachments.length, links.length],
  );

  const handleViewChange = useCallback((id) => {
    setViewMode((prev) => (prev === id ? "chat" : id));
  }, []);

  const scrollToComment = useScrollToComment(comments);

  const handleCommentCreated = useCallback(
    (commentId) => {
      markChatRead({ projectId, taskId });
      scrollToComment(commentId);
    },
    [markChatRead, projectId, taskId, scrollToComment],
  );

  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);

  const handleSearchChange = useCallback((q) => {
    setSearchQuery(q);
    setCurrentMatchIndex(-1);
  }, []);

  const goToMatch = useCallback(
    (delta) => {
      if (!matchSteps.length) return;
      const next =
        currentMatchIndex === -1
          ? delta > 0
            ? 0
            : matchSteps.length - 1
          : (currentMatchIndex + delta + matchSteps.length) % matchSteps.length;
      setCurrentMatchIndex(next);
      scrollToComment(matchSteps[next].commentId);
    },
    [currentMatchIndex, matchSteps, scrollToComment],
  );

  const goToPrevMatch = useCallback(() => goToMatch(-1), [goToMatch]);
  const goToNextMatch = useCallback(() => goToMatch(1), [goToMatch]);

  const currentMatchNumber =
    currentMatchIndex === -1 ? 0 : currentMatchIndex + 1;
  const activeStep =
    currentMatchIndex >= 0 ? matchSteps[currentMatchIndex] : null;

  const employeesById = useMemo(
    () => new Map(employees.map((e) => [e.id, e])),
    [employees],
  );
  const getAuthor = useCallback((id) => employeesById.get(id), [employeesById]);

  const policy = useMemo(
    () => new CommentPolicy({ currentUser, comments }),
    [currentUser, comments],
  );

  const pinnedComments = useMemo(
    () => comments.filter((c) => c.pinned),
    [comments],
  );

  // Мутации — единственный путь записи.
  const { mutate: updateComment } = useUpdateCommentMutation();
  const { mutate: deleteComment } = useDeleteCommentMutation();
  const { mutate: reactComment } = useReactMutation();
  const { mutate: pinComment } = usePinCommentMutation();

  const saveEdit = useCallback(
    (c) => {
      if (!editText.trim()) return;
      updateComment(
        { id: c.id, text: editText.trim() },
        {
          onSuccess: () => {
            setEditingId(null);
            setEditText("");
            toast?.("Комментарий обновлён");
          },
          onError: (err) => toast?.(err.message, "error"),
        },
      );
    },
    [editText, updateComment, toast],
  );

  const togglePin = useCallback(
    (c) => {
      if (!c.pinned && pinnedComments.length >= 5) {
        toast?.("Нельзя закрепить более 5 сообщений", "warning");
        return;
      }
      pinComment(c.id, {
        onSuccess: () =>
          toast?.(
            c.pinned ? "Закрепление снято" : "Комментарий закреплён",
            "success",
          ),
        onError: (err) => toast?.(err.message, "error"),
      });
    },
    [pinnedComments.length, pinComment, toast],
  );

  const setReaction = useCallback(
    (commentId, emoji) => {
      reactComment(
        { id: commentId, emoji },
        { onError: (err) => toast?.(err.message, "error") },
      );
    },
    [reactComment, toast],
  );

  const onDelete = useCallback(
    (c) => {
      deleteComment(c.id, {
        onSuccess: () => toast?.(TOASTS.commentDeleted, "success"),
        onError: (err) => toast?.(err.message, "error"),
      });
    },
    [deleteComment, toast],
  );

  const openLightbox = useCallback(
    (list, index) => setLightbox({ list, index }),
    [],
  );
  const closeLightbox = useCallback(
    () => setLightbox({ list: [], index: null }),
    [],
  );
  const prevLightbox = useCallback(
    () =>
      setLightbox((s) => ({
        ...s,
        index: s.index === 0 ? s.list.length - 1 : s.index - 1,
      })),
    [],
  );
  const nextLightbox = useCallback(
    () =>
      setLightbox((s) => ({
        ...s,
        index: s.index === s.list.length - 1 ? 0 : s.index + 1,
      })),
    [],
  );

  const ctx = {
    currentUser,
    employees,
    getAuthor,
    policy,
    comments,
    visibleComments,
    sortOrder,
    searchQuery,
    activeOccurrence: activeStep,
    editingId,
    setEditingId,
    editText,
    setEditText,
    saveEdit,
    togglePin,
    setReaction,
    onDelete,
    openLightbox,
    showTaskLink,
    onTaskClick,
    tasks,
    onReply: setReplyTo,
    isCommentUnread,
  };

  return (
    <DiscussionProvider value={ctx}>
      <div className="chat">
        <DiscussionHeader
          pinned={pinnedComments}
          onJump={scrollToComment}
          onSearchChange={handleSearchChange}
          totalMatches={matchSteps.length}
          currentMatch={currentMatchNumber}
          onPrevMatch={goToPrevMatch}
          onNextMatch={goToNextMatch}
          viewTabs={viewTabs}
          activeView={viewMode}
          onViewChange={handleViewChange}
          showSearch={viewMode === "chat"}
        />

        {viewMode === "chat" && comments.length > 1 && (
          <SortToolbar value={sortOrder} onChange={setSortOrder} />
        )}

        {viewMode === "chat" && (
          <>
            <CommentTree />

            {visibleComments.length === 0 && searchQuery && (
              <div className="mut sm">Ничего не найдено</div>
            )}
            {comments.length === 0 && !searchQuery && (
              <div className="mut sm">
                Обсуждений пока нет - начните диалог.
              </div>
            )}
          </>
        )}

        {viewMode === "files" && (
          <CommentFiles
            items={attachments}
            getAuthor={getAuthor}
            openLightbox={openLightbox}
          />
        )}

        {viewMode === "links" && (
          <CommentLinks items={links} getAuthor={getAuthor} />
        )}

        {viewMode === "chat" && (
          <CommentComposer
            projectId={projectId}
            taskId={taskId}
            candidates={candidates}
            toast={toast}
            replyTo={replyTo}
            setReplyTo={setReplyTo}
            comments={comments}
            getAuthor={getAuthor}
            onCommentCreated={handleCommentCreated}
          />
        )}

        {lightbox.index !== null && lightbox.list.length > 0 && (
          <Lightbox
            photos={lightbox.list}
            currentIndex={lightbox.index}
            onClose={closeLightbox}
            onPrev={prevLightbox}
            onNext={nextLightbox}
          />
        )}
      </div>
    </DiscussionProvider>
  );
}
